import { ValidationUtility } from "../validation-utility";
import { IValidatorInterface, IValidatorType } from "../validator-interface";

/**
 * Branded ("Networked Plus") mobile app — the community-admin side of /admin-pages/mobile-app.
 *
 * This is an ops intake, not a provisioning trigger: the Flutter app's white-label flavors are
 * hardcoded across six build files plus a CI allowlist, so submitting this form records what the
 * community wants and alerts the team, it does not build anything.
 *
 * Flow order is not the obvious one — the intake form comes BEFORE agreement and payment, so a
 * community can have a fully submitted form while still owing money. That is
 * ModelStatus.MobileAppContractPending (100), which the page renders with a "Contract Pending"
 * badge.
 */

/** Which store a row or transition refers to. Mirrors CommunityMobileAppStore.store. */
export enum MobileAppStoreKind {
	Apple = 0,
	Google = 1,
}

/** The intake fields, mirroring IMobileAppDetails in fe-main's mobileApp.types.ts. */
export interface IMobileAppDetailsPayload {
	appName: string;
	tagline: string;
	description: string;
	category: string;
	/** Store age rating being targeted, e.g. "4+ (All Ages)". */
	ratingTarget: string;
	platformIos: boolean;
	platformAndroid: boolean;
	pushEnabled: boolean;
	/** File-service id + url for the 1024x1024 icon. */
	appIconFileId: string;
	appIconUrl: string;
	/** Literal hex, e.g. "#0D68F9" — the app's own brand colour, not the community web theme. */
	primaryColor: string;
	supportEmail: string;
	/** Invited to Google Play Console. */
	playStoreEmail: string;
	/** Invited to App Store Connect. */
	appleDeveloperEmail: string;
}

export interface IMobileAppStoreRow {
	id: string;
	store: MobileAppStoreKind;
	versionLabel: string;
	/** ModelStatus 110-114. */
	status: number;
	submittedAt: number | null;
	decidedAt: number | null;
	notes: string | null;
}

export interface IMobileAppStatusLogRow {
	id: string;
	fromStatus: number | null;
	toStatus: number;
	store: MobileAppStoreKind | null;
	actorUserId: string | null;
	/** Resolved display name for the actor. Null for system transitions (e.g. the Stripe webhook). */
	actorName: string | null;
	note: string | null;
	timeStamp: number;
}

//Req [GET] /admin/community/mobile-app
export interface ICommunityAdminMobileAppGetRequest { }

//Val [GET] /admin/community/mobile-app — no query params.
export const VCommunityAdminMobileAppGet: IValidatorInterface = {};

/**
 * Drives all five page stages in one call.
 *
 * `configured: false` means this community has never submitted the form — the page renders the
 * landing/upsell stage and every other field is null/empty.
 */
//Res [GET] /admin/community/mobile-app
export interface ICommunityAdminMobileAppResponse {
	configured: boolean;
	/** ModelStatus 100 block, or null when nothing has been submitted yet. */
	status: number | null;
	details: IMobileAppDetailsPayload | null;
	planId: string | null;
	/** ANNUAL amount in minor units — what is actually charged. The monthly figure is derived. */
	priceAnnualAmount: number | null;
	priceCurrency: string | null;
	paidAt: number | null;
	paidOffline: boolean;
	termsAcceptedAt: number | null;
	stores: IMobileAppStoreRow[];
	statusLog: IMobileAppStatusLogRow[];
	createdAt: number | null;
	updatedAt: number | null;
}

/**
 * Submit or update the intake.
 *
 * Editing stays open at every status, deliberately. Fulfilment is manual and takes weeks, so a
 * customer fixing a tagline or swapping the app icon mid-build is ordinary; blocking it would
 * only produce support tickets. The risk is not the edit, it is nobody telling the person
 * building the app — so an edit made at or past MobileAppInDevelopment re-alerts the team and
 * writes a status-log row instead of being rejected.
 *
 * `planId` is the exception: once payment has landed it is a price change, and the page routes
 * that to Contact Support rather than accepting it here.
 */
//Req [POST] /admin/community/mobile-app
export interface ICommunityAdminMobileAppSetRequest extends IMobileAppDetailsPayload {
	/** Only honoured before payment. Ignored (not rejected) afterwards. */
	planId?: string;
}

//Val [POST] /admin/community/mobile-app
export const VCommunityAdminMobileAppSet: IValidatorType<ICommunityAdminMobileAppSetRequest> = {
	appName: { function: ValidationUtility.validateStringFields, params: [1, 100], required: true },
	tagline: { function: ValidationUtility.validateStringFields, params: [0, 200], required: false },
	description: { function: ValidationUtility.validateStringFields, params: [0, 4000], required: false },
	category: { function: ValidationUtility.validateStringFields, params: [0, 100], required: false },
	ratingTarget: { function: ValidationUtility.validateStringFields, params: [0, 100], required: false },
	platformIos: { function: ValidationUtility.validateBoolean, params: [], required: false },
	platformAndroid: { function: ValidationUtility.validateBoolean, params: [], required: false },
	pushEnabled: { function: ValidationUtility.validateBoolean, params: [], required: false },
	appIconFileId: { function: ValidationUtility.validateStringFields, params: [0, 200], required: false },
	appIconUrl: { function: ValidationUtility.validateStringFields, params: [0, 2000], required: false },
	primaryColor: { function: ValidationUtility.validateStringFields, params: [0, 20], required: false },
	supportEmail: { function: ValidationUtility.validateEmail, params: [3, -1], required: false },
	playStoreEmail: { function: ValidationUtility.validateEmail, params: [3, -1], required: false },
	appleDeveloperEmail: { function: ValidationUtility.validateEmail, params: [3, -1], required: false },
	planId: { function: ValidationUtility.validateStringFields, params: [0, 50], required: false },
};

//Res [POST] /admin/community/mobile-app
export interface ICommunityAdminMobileAppSetResponse {
	configured: boolean;
	status: number;
	/** True when this call created the record rather than updating one. */
	created: boolean;
}

/**
 * Record acceptance of the plan agreement.
 *
 * `termsSnapshot` is the agreement text as the customer saw it, stored alongside the acceptance
 * rather than referenced. The only other terms field in the schema
 * (CommunityProfile.termsCondition) is a single mutable string with no version identifier, so an
 * acceptance pointing at it would be unfalsifiable — the text can change underneath the record.
 */
//Req [POST] /admin/community/mobile-app/terms
export interface ICommunityAdminMobileAppTermsRequest {
	planId: string;
	termsSnapshot: string;
}

//Val [POST] /admin/community/mobile-app/terms
export const VCommunityAdminMobileAppTerms: IValidatorType<ICommunityAdminMobileAppTermsRequest> = {
	planId: { function: ValidationUtility.validateStringFields, params: [1, 50], required: true },
	termsSnapshot: { function: ValidationUtility.validateStringFields, params: [1, -1], required: true },
};

//Res [POST] /admin/community/mobile-app/terms
export interface ICommunityAdminMobileAppTermsResponse {
	termsAcceptedAt: number;
	planId: string;
}

/**
 * Branded-app upsell prompt — the "should this admin be nudged" decision, made server-side so
 * the counts, the purchase state and the per-admin show counter all live in one place.
 *
 * Sent on GET /community/one (admins only) as `brandedAppPrompt`. Clients only decide *when* to
 * show the popup (never on launch — stores flag payment-style prompts there); whether to show
 * it at all is this object.
 */
export interface IBrandedAppPrompt {
	/** Community has the traction to be offered the app and has no active request. */
	eligible: boolean;
	/** Popup may be shown this session: eligible, under maxShows, cooldown passed, CTA never tapped. */
	showPopup: boolean;
	/**
	 * Side-menu entry should be shown. True while eligible (even after the popup is used up) and
	 * while a request is in progress (100–103), so the admin can follow it. False once live.
	 */
	showDrawerCard: boolean;
	/** CommunityMobileApp.status when a non-cancelled request exists (ModelStatus 100 block). */
	requestStatus: number | null;
	/** Seconds of active foreground use before the client may show the popup. */
	delaySeconds: number;
	shownCount: number;
	maxShows: number;
}

/**
 * Client platform header, so a store-review kill switch can target one platform.
 * Absent means web.
 */
export const BRANDED_APP_PLATFORM_HEADER = "x-client-platform";

export enum BrandedAppPromptAction {
	Shown = 0,
	Dismissed = 1,
	Cta = 2,
}

/**
 * Send `Shown` the moment the popup appears (not on close) so the maxShows limit holds even if
 * the app is killed while it is open. `Cta` stops the popup for good; the drawer card stays.
 */
//Req [POST] /admin/community/mobile-app/prompt-event
export interface ICommunityAdminMobileAppPromptEventRequest {
	action: BrandedAppPromptAction;
}

//Val [POST] /admin/community/mobile-app/prompt-event
export const VCommunityAdminMobileAppPromptEvent: IValidatorType<ICommunityAdminMobileAppPromptEventRequest> = {
	action: { function: ValidationUtility.validateEnum, params: [BrandedAppPromptAction], required: true },
};

//Res [POST] /admin/community/mobile-app/prompt-event
export interface ICommunityAdminMobileAppPromptEventResponse {
	shownCount: number;
	maxShows: number;
	/** Whether the popup could still be shown again later (after the cooldown). */
	canShowAgain: boolean;
}

/**
 * Scheduled reminder for owners who qualify for the branded-app offer but haven't opened the app
 * to see it. Service-only: authenticated by BRANDED_APP_REMINDER_SERVICE_HEADER, not a session.
 * The push is NotificationName.BrandedAppPromptReminder; its `ids` carry
 * IBrandedAppPromptReminderIds so the app can open the offer for the right community.
 */
export const BRANDED_APP_REMINDER_SERVICE_HEADER = "x-networked-service-token";

/** `ids` on the reminder notification (NotificationV2.ids and the push payload). */
export interface IBrandedAppPromptReminderIds {
	communityId: string;
	/** Marks the tap as "open the branded-app offer" rather than a plain community open. */
	brandedAppPrompt: true;
}

//Req [POST] /admin/community/mobile-app/prompt-reminders
export interface ICommunityAdminMobileAppPromptRemindersRequest {
	/** Evaluate and report without writing state or sending anything. */
	dryRun?: boolean;
	/** Cap on communities scanned in this run (defaults to BrandedAppPromptReminderConfig.maxPerRun). */
	limit?: number;
}

//Val [POST] /admin/community/mobile-app/prompt-reminders
export const VCommunityAdminMobileAppPromptReminders: IValidatorType<ICommunityAdminMobileAppPromptRemindersRequest> = {
	dryRun: { function: ValidationUtility.validateBoolean, params: [], required: false },
	limit: { function: ValidationUtility.validateNumber, params: [], required: false },
};

//Res [POST] /admin/community/mobile-app/prompt-reminders
export interface ICommunityAdminMobileAppPromptRemindersResponse {
	dryRun: boolean;
	/** Communities looked at (set up, no open branded-app request). */
	scanned: number;
	/** Of those, communities whose owner would currently get the popup. */
	eligible: number;
	/** Reminders sent (or that would be sent, on a dry run). */
	sent: number;
	/** First time the job saw the community eligible: the waiting period starts now. */
	started: number;
	/** Eligible but skipped: too soon, already reminded enough, or recently reminded. */
	waiting: number;
	failed: number;
	/** Community ids reminded in this run. */
	communityIds: string[];
}
